import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

/** Months stacked in the mobile sheet; more are added as the user nears either end. */
const INITIAL_MONTHS = 6;
const MONTHS_PER_LOAD = 6;
const LOAD_THRESHOLD_PX = 600;

/** Nearest ancestor that scrolls, so distances are measured against the sheet, not the page. */
const getScrollParent = (node: HTMLElement) => {
  let parent = node.parentElement;
  while (parent && getComputedStyle(parent).overflowY !== 'auto') {
    parent = parent.parentElement;
  }
  return parent;
};

interface UseMobileMonthScrollOptions {
  isOpen: boolean;
  /** Identifies the month the calendar is anchored to; a change re-anchors the view. */
  anchorKey: string;
}

/**
 * Endless month scrolling for the mobile date sheet.
 *
 * Returns how many months to render, how many of them sit before the anchor month, and a ref
 * callback for an empty element inside the scroll container. Months are appended near the
 * bottom and prepended near the top (with the scroll position restored so the view doesn't jump).
 */
export default function useMobileMonthScroll({ isOpen, anchorKey }: UseMobileMonthScrollOptions) {
  const [futureMonthCount, setFutureMonthCount] = useState(INITIAL_MONTHS);
  const [pastMonthCount, setPastMonthCount] = useState(0);

  const scrollerRef = useRef<HTMLElement | null>(null);
  // Prepending months pushes content down, so restore the scroll position afterwards.
  const scrollAnchor = useRef<{ scroller: HTMLElement; height: number; top: number } | null>(null);
  const isLoadingPast = useRef(false);
  const isLoadingFuture = useRef(false);
  const scrollCleanup = useRef<(() => void) | null>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: re-run after each prepend
  useLayoutEffect(() => {
    const anchor = scrollAnchor.current;
    if (anchor) {
      anchor.scroller.scrollTop = anchor.top + (anchor.scroller.scrollHeight - anchor.height);
      scrollAnchor.current = null;
    }
    isLoadingPast.current = false;
  }, [pastMonthCount]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: re-arm after each append
  useEffect(() => {
    isLoadingFuture.current = false;
  }, [futureMonthCount]);

  // Start every session at the anchor month. Counts reset on close so the next open never renders
  // with the previous session's prepended months, and a new anchor month (trip dates landing
  // while open) re-anchors the view instead of leaving the scroll offset pointing at old months.
  // biome-ignore lint/correctness/useExhaustiveDependencies: anchorKey is the intended trigger
  useLayoutEffect(() => {
    setPastMonthCount(0);
    isLoadingPast.current = false;
    if (!isOpen) {
      setFutureMonthCount(INITIAL_MONTHS);
      return;
    }
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = 0;
    }
  }, [isOpen, anchorKey]);

  // Scroll listener rather than IntersectionObserver: it keeps working however the sheet's
  // scroll container is nested, and the loading flags stop a burst of scroll events from
  // queueing several loads before React re-renders.
  const attachScrollLoader = useCallback((node: HTMLDivElement | null) => {
    scrollCleanup.current?.();
    scrollCleanup.current = null;
    const scroller = node && getScrollParent(node);
    scrollerRef.current = scroller;
    if (!scroller) {
      return;
    }
    const loadMoreIfNeeded = () => {
      const distanceToBottom = scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight;
      if (distanceToBottom < LOAD_THRESHOLD_PX && !isLoadingFuture.current) {
        isLoadingFuture.current = true;
        setFutureMonthCount((count) => count + MONTHS_PER_LOAD);
      }
      if (scroller.scrollTop < LOAD_THRESHOLD_PX && !isLoadingPast.current) {
        isLoadingPast.current = true;
        scrollAnchor.current = {
          scroller,
          height: scroller.scrollHeight,
          top: scroller.scrollTop,
        };
        setPastMonthCount((count) => count + MONTHS_PER_LOAD);
      }
    };
    scroller.addEventListener('scroll', loadMoreIfNeeded, { passive: true });
    const frame = requestAnimationFrame(loadMoreIfNeeded);
    scrollCleanup.current = () => {
      scroller.removeEventListener('scroll', loadMoreIfNeeded);
      cancelAnimationFrame(frame);
    };
  }, []);

  return { monthCount: pastMonthCount + futureMonthCount, pastMonthCount, attachScrollLoader };
}
